import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useModel, useSelector} from 'umi';
import CustomerDrawer from '@/components/CustomerDrawer';
import UserCreateDrawer from '@/components/UserCreateDrawer';
import PromotionDrawer from '@/components/PromotionDrawer';
import LeadDetailsDrawer from '@/components/Lead/LeadDetailsDrawer';
import ContactDrawer from '@/components/ContactDrawer';
import CreateServiceRequestDrawer from '@/components/Kanban/ServiceRequests/CreateServiceRequestDrawer';
import PERMISSIONS from '@/constants/permissions';
import {CheckAccess, getSolutionFromPath, hasAccess} from '@/utils/access';
import AddLeadDrawer from '@/components/LeadSource/AddLeadDrawer';
import ServiceTypesDrawer from '@/components/LocationDrawers/ServiceTypesDrawer';
import * as JourneySelectors from '@/selectors/journeySelectors';
import {SOLUTION_KEYS} from '@/constants/solutions';
import TaskAutomationDrawer from '@/components/TaskAutomation/TaskAutomationDrawer';
import CreateTaskDrawer from '@/pages/Tasks/CreateTaskDrawer';
import TaskDetailsDrawer from '@/components/Tasks/TaskDetailsDrawer';
import {LeadAssigneeOptionsProvider} from '@/contexts/LeadAssigneeOptionsContext';
import ServiceRequestDetailsDrawer from '@/components/Kanban/ServiceRequests/ServiceRequestDetailsDrawer';
import {usePubSubEvent} from '@/context/PubSubContext/hooks';
import {
  APPOINTMENT_CHANGED,
  FETCH_ME,
  SERVICE_AGENTS_CHANGED,
  ServiceAgentsChangedPayload,
} from '@/context/PubSubContext/events';
import {fetchMeRequest} from '@/services/api/user';
import {processAPIResponseNow} from '@/utils/request';
import AgentDrawer from '@/components/Agent/AgentDrawer';
import HelpArticleDrawer from '@/components/HelpCenter/ArticleDrawer';
import CalendarEventDetailsDrawer from '@/components/Kanban/ServiceRequests/CalendarEventDetailsDrawer';
import BookingPageDrawer from '@/components/BookingPage/BookingPageDrawer';
import NewBranchDrawer from '@/components/LocationDrawers/BranchDrawer';
import ProductGroupDrawer from '@/components/ProductGroupDrawer';
import SkillsDrawer from '@/components/SkillsDrawer';
import {SKILLS_ACCESS} from '@/components/SkillsDrawer/constants';
import TemplateDrawer from '@/pages/EmailCenter/tabs/Templates/TemplateDrawer';
import TemplatePreviewDrawer from '@/pages/EmailCenter/tabs/Templates/TemplatePreviewDrawer';
import EmailConfigDrawer from '@/pages/EmailCenter/tabs/JourneyEmails/EmailConfigDrawer';
import LinkTemplateDrawer from '@/pages/EmailCenter/tabs/JourneyEmails/LinkTemplateDrawer';
import MailerDrawer from '@/pages/EmailCenter/tabs/Settings/Mailers/MailerDrawer';
import TranslationDrawer from '@/pages/EmailCenter/tabs/Settings/Translations/TranslationDrawer';
import AutomationDrawer from '@/pages/EmailCenter/tabs/Automations/AutomationDrawer';
import {DRAWER_ACCESS} from '@/pages/EmailCenter/constants';


// const journeyWatcherActions = ["serviceRequest/fetch", "customers/fetch", "customers/fetchStates", "analytics/init", "location/init", {
//   type: "serviceRequest/fetchInfinite",
//   payload: {
//     reset: true
//   }
// }]

const DvaWatcher: React.FC = () => {
  const dispatch = useDispatch();
  // @ts-ignore
  const store: DefaultRootState = useSelector((store) => store);
  const { initialState, setInitialState } = useModel('@@initialState');
  const [initialized, setInitialized] = useState(false);
  const conversionalLeadGenerationJourneys = useSelector(JourneySelectors.selectConversionalJourneys);
  const journeysInitialized = useSelector(JourneySelectors.selectJourneysInitialized);
  const selectedJourney = useSelector(JourneySelectors.selectSelectedJourney);

  const customerDrawer = store.customers.drawer;


  usePubSubEvent(FETCH_ME, () => {
    fetchMeRequest().then(response => processAPIResponseNow(response, {
      onSuccess: (data) => {
        setInitialState({
          ...initialState,
          currentUser: {
            ...initialState?.currentUser,
            ...data,
          }
        })
      }
    }))
  })

  // Subscribed here (always mounted) rather than in the calendar: the assignment can
  // change while the user is on any page, and the calendar's own branch fetch is lazy.
  const refetchBranchesForServiceAgents = useCallback(
    (payload: ServiceAgentsChangedPayload) =>
      dispatch({ type: 'location/refetchBranchesForServices', payload }),
    [dispatch],
  );

  usePubSubEvent<ServiceAgentsChangedPayload>(SERVICE_AGENTS_CHANGED, refetchBranchesForServiceAgents);

  // Same reason: an appointment can be deleted or cancelled from a drawer stacked on
  // top of the calendar, or from another page entirely. The calendar watches this
  // timestamp and refetches its events.
  const refreshCalendarView = useCallback(
    () => dispatch({ type: 'serviceRequest/updateFetchCalendarViewAt' }),
    [dispatch],
  );

  usePubSubEvent(APPOINTMENT_CHANGED, refreshCalendarView);

  useEffect(() => {
    if (!hasAccess(initialState.currentUser, PERMISSIONS.BUSINESS_PARTNER)) return;
    dispatch({
      type: 'journey/fetchConfig',
      payload: { journeyId: selectedJourney?.hashId },
    });
  }, [selectedJourney?.hashId]);

  useEffect(() => {
    if (initialState && initialState?.currentUser?.Journeys) {
      dispatch({
        type: 'journey/gotJourneys',
        payload: initialState.currentUser.Journeys,
      });
    }
  }, [initialState]);

  useEffect(() => {
    const pathSolution = getSolutionFromPath(window.location.pathname);
    if (journeysInitialized && initialState?.selectedSolution === SOLUTION_KEYS.LEAD_GENERATION && Array.isArray(conversionalLeadGenerationJourneys) && conversionalLeadGenerationJourneys?.length === 0) {
      if (pathSolution === SOLUTION_KEYS.LEAD_GENERATION){
        setInitialState({
          ...initialState,
          selectedSolution: SOLUTION_KEYS.LEAD_GENERATION,
        });
        return
      }

      if (initialState.solutions?.length > 1) {
        setInitialState({
          ...initialState,
          selectedSolution: SOLUTION_KEYS.LEAD_MANAGEMENT,
        });
      }
      return;
    }
    if (initialState?.selectedSolution && initialized) {
      dispatch({
        type: 'preferences/update',
        payload: {
          key: 'selectedSolution',
          value: {
            value: initialState.selectedSolution,
          },
        },
      });
    }
    setInitialized(true);
  }, [initialState?.selectedSolution, journeysInitialized, conversionalLeadGenerationJourneys]);

  useEffect(() => {
    setInitialState({
      ...initialState,
      config: { ...initialState?.config, ...store?.journey?.config },
    });
  }, [JSON.stringify(store?.journey?.config)]);
  return (
    <LeadAssigneeOptionsProvider>
      {!customerDrawer?.nested && <CustomerDrawer />}
      <NewBranchDrawer />
      {/* <OldBranchDrawer /> */}
      <PromotionDrawer />
      <CheckAccess
        access={[
          PERMISSIONS.Users.CREATE,
          PERMISSIONS.Users.EDIT,
          PERMISSIONS.BUSINESS_PARTNER,
          PERMISSIONS.Roles.VIEW,
        ]}
      >
        <UserCreateDrawer />
      </CheckAccess>
      <AddLeadDrawer />
      <LeadDetailsDrawer />
      <ContactDrawer />
      <CreateServiceRequestDrawer />
      <ServiceTypesDrawer />
      <TaskAutomationDrawer />
      <CreateTaskDrawer />
      <TaskDetailsDrawer />
      <ServiceRequestDetailsDrawer />
      <CalendarEventDetailsDrawer />
      <AgentDrawer />
      <HelpArticleDrawer />
      <ProductGroupDrawer />
      <CheckAccess access={SKILLS_ACCESS.view}>
        <SkillsDrawer />
      </CheckAccess>
      <CheckAccess
        access={[
          PERMISSIONS.BOOKING_PAGES.MANAGE,
          PERMISSIONS.BUSINESS_PARTNER,
        ]}
      >
        <BookingPageDrawer />
      </CheckAccess>
      {/* One gate per touchpoint entity, not one for the group: these were all
          behind `[BUSINESS_PARTNER]`, so every granular Touchpoints permission
          granted a page whose every button then did nothing. The sets live in
          `DRAWER_ACCESS` so they can be asserted without mounting a drawer. */}
      <CheckAccess access={[...DRAWER_ACCESS.template]}>
        <TemplateDrawer />
        <TemplatePreviewDrawer />
      </CheckAccess>
      <CheckAccess access={[...DRAWER_ACCESS.journeyEmails]}>
        <EmailConfigDrawer />
        <LinkTemplateDrawer />
        <AutomationDrawer />
      </CheckAccess>
      <CheckAccess access={[...DRAWER_ACCESS.mailer]}>
        <MailerDrawer />
      </CheckAccess>
      <CheckAccess access={[...DRAWER_ACCESS.translation]}>
        <TranslationDrawer />
      </CheckAccess>
    </LeadAssigneeOptionsProvider>
  );
};

export default DvaWatcher;
